'use client';

import NextLink from 'next/link';
import { useTranslations, useLocale } from 'next-intl';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Divider,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import CodeIcon from '@mui/icons-material/Code';
import GroupsIcon from '@mui/icons-material/Groups';
import { LandingHeader, LandingFooter } from '../../../features/landing';

/**
 * AboutPageContent - Client component for the About page
 *
 * Tells the story of Boty:
 * - What Boty is and does
 * - The story of building Boty with AI agents
 * - The BMAD_METHOD and Claude Code collaboration
 *
 * Story 6.7: Landing Page Header And Footer For Credibility
 */
export function AboutPageContent() {
  const t = useTranslations('creator.about');
  const locale = useLocale();

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <LandingHeader />

      <Container maxWidth="md" sx={{ py: { xs: 4, md: 6 }, flex: 1 }}>
        {/* Page Title */}
        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            mb: 4,
            textAlign: 'center',
          }}
        >
          {t('title')}
        </Typography>

        {/* What is Boty Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <SmartToyIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {t('whatIsBoty')}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('whatIsBotyDescription')}
          </Typography>
        </Paper>

        {/* The Story Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            bgcolor: 'grey.50',
            borderRadius: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <GroupsIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {t('theStory')}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('theStoryDescription')}
          </Typography>
        </Paper>

        {/* Built with AI Section */}
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            bgcolor: 'primary.50',
            borderRadius: 2,
            border: 1,
            borderColor: 'primary.100',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            <CodeIcon color="primary" sx={{ fontSize: 32 }} />
            <Typography variant="h5" component="h2" sx={{ fontWeight: 600 }}>
              {t('builtWithAI')}
            </Typography>
          </Box>
          <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            {t('builtWithAIDescription')}
          </Typography>
        </Paper>

        <Divider sx={{ my: 4 }} />

        {/* Back to Home Button */}
        <Box sx={{ textAlign: 'center' }}>
          <Button
            component={NextLink}
            href={`/${locale}`}
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            size="large"
            sx={{ textTransform: 'none' }}
          >
            {t('backToHome')}
          </Button>
        </Box>
      </Container>

      <LandingFooter />
    </Box>
  );
}

export default AboutPageContent;
